import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  Section, Inner, SectionHeader, SectionTitle, SectionSubtitle, Divider, Grid, Card, Badge,
} from '../styles/v2.styled';
import useLMSAuth from '../hooks/useLMSAuth';
import xrDeviceGrid from '../../image/xr-device-selector-grid.png';
import simulationCourseGrid from '../../image/courses/course-simulation-grid.png';

const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.neutral[0]};
`;
const Hero = styled.section`
  padding: 70px 0 40px;
  background: linear-gradient(135deg, #134e4a, #0f766e);
  color: #fff; text-align: center;
`;
const HeroTitle = styled.h1`font-size: 2.2rem; margin: 0 0 12px; font-weight: 800;`;
const HeroSub = styled.p`font-size: 1rem; opacity: 0.85;`;

const Thumb = styled.div`
  width: 100%; height: 160px;
  position: relative; overflow: hidden;
  background-image: ${({ $url }) => `linear-gradient(180deg, rgba(2,6,23,.02), rgba(2,6,23,.48)), url(${$url || simulationCourseGrid})`};
  background-size: ${({ $url }) => $url ? 'cover' : '300% 100%'};
  background-position: ${({ $url, $position }) => $url ? 'center' : ($position || '0% center')};
  background-repeat: no-repeat;
  display: flex; align-items: center; justify-content: center; color: #fff; font-size: 2rem;
  &::after { content: 'XR SIMULATION'; position: absolute; left: 14px; bottom: 11px; font-size: 10px; font-weight: 900; letter-spacing: .16em; color: #a5f3fc; text-shadow: 0 2px 12px #020617; }
`;
const Body = styled.div`padding: 18px 20px 22px;`;
const Title = styled.div`font-size: 1.05rem; font-weight: 700; margin-bottom: 6px;`;
const Meta = styled.div`font-size: 0.83rem; color: #6b7280; margin-bottom: 8px;`;
const PlayBtn = styled.button`
  width: 100%; padding: 10px; font-weight: 600; border-radius: 8px; border: none;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary[500]}, ${({ theme }) => theme.colors.primary[700]});
  color: #fff; cursor: pointer; margin-top: 10px;
`;
const ActionRow = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 10px;
  ${PlayBtn} { margin-top: 0; }
`;
const XRBtn = styled.button`
  padding: 10px 8px; font-weight: 700; border-radius: 8px;
  border: 1px solid #22d3ee; color: #0e7490; background: #ecfeff;
  cursor: pointer; transition: 0.2s;
  &:hover { color: #fff; background: linear-gradient(135deg, #0891b2, #7c3aed); border-color: transparent; }
`;

const Modal = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.85);
  display: flex; align-items: center; justify-content: center; z-index: 9999;
`;
const VideoWrap = styled.div`
  background: #000; border-radius: 12px; width: 92%; max-width: 960px;
  position: relative;
`;
const Close = styled.button`
  position: absolute; top: -36px; right: 0; background: none; border: none;
  color: #fff; font-size: 1.5rem; cursor: pointer;
`;
const NoVideo = styled.div`
  padding: 60px; color: #d1d5db; text-align: center; font-size: 0.95rem;
`;
const XRPanel = styled.div`
  width: min(680px, calc(100vw - 28px)); max-height: calc(100vh - 40px); overflow-y: auto;
  padding: 24px; border-radius: 18px; color: #e2e8f0;
  background: radial-gradient(circle at top right, rgba(124,58,237,.28), transparent 42%), #07111f;
  border: 1px solid rgba(34,211,238,.4); box-shadow: 0 28px 80px rgba(0,0,0,.55);
`;
const XRHead = styled.div`
  display: flex; justify-content: space-between; gap: 16px; align-items: flex-start;
  h2 { margin: 0 0 6px; font-size: 1.35rem; } p { margin: 0; color: #94a3b8; line-height: 1.5; }
`;
const XRClose = styled.button`
  border: 0; background: rgba(255,255,255,.08); color: #fff; width: 36px; height: 36px;
  border-radius: 50%; cursor: pointer; font-size: 18px;
`;
const XRApps = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 10px; margin-top: 20px;
  @media(max-width: 560px){ grid-template-columns: 1fr; }
`;
const XRApp = styled.button`
  text-align: left; padding: 0; overflow: hidden; border-radius: 12px; cursor: pointer; color: #e2e8f0;
  border: 1px solid ${({ $selected }) => $selected ? '#22d3ee' : 'rgba(148,163,184,.25)'};
  background: ${({ $selected }) => $selected ? 'rgba(8,145,178,.18)' : 'rgba(15,23,42,.72)'};
  box-shadow: ${({ $selected }) => $selected ? '0 0 0 2px rgba(34,211,238,.16), 0 12px 28px rgba(8,145,178,.18)' : 'none'};
  transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease;
  &:hover { transform: translateY(-2px); border-color: rgba(34,211,238,.72); }
  .content { padding: 13px 14px 15px; }
  .top { display:flex; justify-content:space-between; gap:8px; font-weight:800; }
  .desc { color:#94a3b8; font-size:12px; margin-top:6px; line-height:1.45; }
  .ready { color:#fbbf24; font-size:11px; border:1px solid rgba(251,191,36,.4); border-radius:99px; padding:2px 7px; white-space:nowrap; }
`;
const XRDeviceImage = styled.div`
  position: relative; width: 100%; aspect-ratio: 3 / 2;
  background-image: url(${xrDeviceGrid});
  background-size: 200% 200%;
  background-position: ${({ $position }) => $position};
  background-repeat: no-repeat;
  border-bottom: 1px solid rgba(148,163,184,.2);
  &::after {
    content: ${({ $selected }) => $selected ? "'✓ 선택됨'" : "''"};
    position: absolute; top: 9px; right: 9px; padding: 4px 8px; border-radius: 999px;
    background: rgba(8,145,178,.9); color: #fff; font-size: 11px; font-weight: 900;
  }
`;
const XRSelection = styled.div`
  margin-top: 16px; padding: 12px 14px; border-radius: 10px; color: #bae6fd;
  background: rgba(14,116,144,.18); border: 1px solid rgba(34,211,238,.25); font-size: 13px;
`;

const NotLogin = styled.div`
  padding: 60px 28px; text-align: center; background: #f9fafb; border-radius: 12px;
  color: #6b7280;
`;

export default function MyClassroomPage() {
  const { login, role, loading, name } = useLMSAuth();
  const [list, setList] = useState([]);
  const [video, setVideo] = useState(null); // { url, title }
  const [xrCourse, setXrCourse] = useState(null);
  const [xrApp, setXrApp] = useState('');
  const xrApps = [
    { id: 'meta-quest', position: '0% 0%', name: 'Meta Quest', desc: 'Quest 3·3S 혼합현실 앱으로 교실과 시뮬레이터를 연결합니다.' },
    { id: 'webxr', position: '100% 0%', name: 'WebXR 뷰어', desc: '설치 없이 지원 브라우저에서 XR 실습 환경을 실행합니다.' },
    { id: 'android-xr', position: '0% 100%', name: 'Android XR', desc: 'Android XR 헤드셋과 공간 컴퓨팅 앱 연결을 준비합니다.' },
    { id: 'vision-pro', position: '100% 100%', name: 'Apple Vision Pro', desc: 'visionOS용 공간형 실습 앱과 연동할 예정입니다.' },
  ];
  const courseBackgroundPosition = (course, index) => {
    const key = `${course.simulation_key || ''} ${course.title || ''}`.toLowerCase();
    if (/diagnos|inspection|ai|진단|검사/.test(key)) return '50% center';
    if (/semi|fab|wafer|반도체|웨이퍼/.test(key)) return '100% center';
    if (/robot|automation|로봇|자동화/.test(key)) return '0% center';
    return ['0% center', '50% center', '100% center'][index % 3];
  };

  const load = async () => {
    if (!login) return;
    const r = await fetch('/lms/my-classroom', { credentials: 'include' });
    if (r.ok) {
      const courses = await r.json();
      const semiconductorFirst = [...courses].sort((a, b) => {
        const priority = (course) => /semi|fab|wafer|반도체|웨이퍼/i.test(
          `${course.simulation_key || ''} ${course.title || ''}`
        ) ? 0 : 1;
        return priority(a) - priority(b);
      });
      setList(semiconductorFirst);
    }
  };
  useEffect(() => { load(); }, [login]);

  const play = async (course) => {
    const r = await fetch(`/lms/courses/${course.id}/video`, { credentials: 'include' });
    if (r.status === 403) { alert('수강 승인이 필요합니다.'); return; }
    if (!r.ok) { alert('영상 정보를 불러올 수 없습니다.'); return; }
    const d = await r.json();
    setVideo({ url: d.video_url, title: course.title });
  };

  const launchSimulation = async (course) => {
    const r = await fetch(`/lms/courses/${course.id}/simulation-launch`, {
      method: 'POST', credentials: 'include',
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { alert(d.error || '시뮬레이션 입장권을 발급하지 못했습니다.'); return; }
    window.location.assign(d.launch_url);
  };

  if (loading) return <PageWrapper><Section><Inner>로딩 중...</Inner></Section></PageWrapper>;

  return (
    <PageWrapper>
      <Hero>
        <HeroTitle>내 강의실</HeroTitle>
        <HeroSub>{login ? `${name || ''}님의 승인된 수강 과정` : '로그인 후 이용 가능합니다'}</HeroSub>
      </Hero>

      <Section>
        <Inner>
          {!login ? (
            <NotLogin>
              <p>로그인 후 수강 신청한 과정을 시청할 수 있습니다.</p>
              <Link to="/Login?next=/MyClassroom" style={{ color: '#0891b2', fontWeight: 700 }}>로그인하고 내 수업 보기 →</Link>
            </NotLogin>
          ) : list.length === 0 ? (
            <NotLogin>
              <p>승인된 수강 과정이 없습니다.</p>
              <Link to="/Enroll" style={{ color: '#0891b2', fontWeight: 700 }}>수강 신청하러 가기 →</Link>
            </NotLogin>
          ) : (
            <>
              <SectionHeader>
                <Divider $center />
                <SectionTitle>내 강의 ({list.length})</SectionTitle>
                <SectionSubtitle>승인된 과정만 시청할 수 있습니다 (권한: {role})</SectionSubtitle>
              </SectionHeader>
              <Grid $cols={3} $colsTablet={2} $colsMobile={1}>
                {list.map((c, index) => (
                  <Card key={c.id}>
                    <Thumb $url={c.thumbnail_url} $position={courseBackgroundPosition(c, index)} />
                    <Body>
                      <Badge $variant="success">수강중</Badge>
                      <Title style={{ marginTop: 8 }}>{c.title}</Title>
                      <Meta>강사: {c.instructor_name || '-'}</Meta>
                      <Meta>승인일: {(c.processed_at || '').slice(0, 10)}</Meta>
                      {c.simulation_key ? (
                        <ActionRow>
                          <PlayBtn onClick={() => launchSimulation(c)}>시뮬레이션 입장</PlayBtn>
                          <XRBtn onClick={() => { setXrCourse(c); setXrApp(''); }}>XR 장비 연결</XRBtn>
                        </ActionRow>
                      ) : (
                        <PlayBtn onClick={() => play(c)}>강의 시청</PlayBtn>
                      )}
                    </Body>
                  </Card>
                ))}
              </Grid>
            </>
          )}
        </Inner>
      </Section>

      {video && (
        <Modal onClick={() => setVideo(null)}>
          <VideoWrap onClick={(e) => e.stopPropagation()}>
            <Close onClick={() => setVideo(null)}>✕ 닫기</Close>
            {video.url ? (
              video.url.includes('youtu') ? (
                <iframe
                  src={video.url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                  width="100%" height="540" allow="autoplay; encrypted-media" allowFullScreen
                  style={{ border: 0, borderRadius: 12 }} title={video.title}
                />
              ) : (
                <video src={video.url} controls autoPlay style={{ width: '100%', borderRadius: 12 }} />
              )
            ) : (
              <NoVideo>
                <p>"{video.title}" 강의 영상이 아직 등록되지 않았습니다.</p>
                <p style={{ fontSize: '0.85rem' }}>강사가 비디오 URL을 등록하면 시청할 수 있습니다.</p>
              </NoVideo>
            )}
          </VideoWrap>
        </Modal>
      )}
      {xrCourse && (
        <Modal onClick={() => setXrCourse(null)}>
          <XRPanel onClick={(e) => e.stopPropagation()}>
            <XRHead>
              <div><h2>XR 앱 선택</h2><p>{xrCourse.title}<br/>사용할 증강·혼합현실 앱을 선택하세요.</p></div>
              <XRClose onClick={() => setXrCourse(null)} aria-label="닫기">×</XRClose>
            </XRHead>
            <XRApps>
              {xrApps.map((app) => (
                <XRApp key={app.id} $selected={xrApp === app.id} onClick={() => setXrApp(app.id)}>
                  <XRDeviceImage $position={app.position} $selected={xrApp === app.id} role="img" aria-label={`${app.name} 장비 이미지`} />
                  <div className="content">
                    <div className="top"><span>{app.name}</span><span className="ready">준비 중</span></div>
                    <div className="desc">{app.desc}</div>
                  </div>
                </XRApp>
              ))}
            </XRApps>
            <XRSelection>{xrApp ? `${xrApps.find(app => app.id === xrApp)?.name}을 선택했습니다. 기기 연결 기능은 준비 중입니다.` : '연결할 XR 앱을 선택해 주세요.'}</XRSelection>
          </XRPanel>
        </Modal>
      )}
    </PageWrapper>
  );
}
